/**
 * ReportShowcase — Premium renderer for report / audit / diagnostic layouts.
 * Palette: amber primary + red critical + emerald OK.
 * Migrated to Athena Presentation Design System (Phase 7.5).
 */
import React from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:      '#fbbf24',
  accentFade:  'rgba(251,191,36,0.10)',
  accentBdr:   'rgba(251,191,36,0.22)',
  danger:      '#ef4444',
  dangerFade:  'rgba(239,68,68,0.10)',
  dangerBdr:   'rgba(239,68,68,0.25)',
  success:     '#34d399',
  successFade: 'rgba(52,211,153,0.10)',
  info:        '#60a5fa',
  infoFade:    'rgba(96,165,250,0.10)',
  warning:     '#f97316',
  card:        base.card,
  border:      base.border,
  text:        base.text,
  textMuted:   base.textMuted,
  textFaint:   base.textFaint,
};

const SEV = {
  critical: { label: 'Crítico', color: C.danger,  bg: C.dangerFade,  border: C.dangerBdr },
  high:     { label: 'Alto',    color: C.warning,  bg: 'rgba(249,115,22,.1)', border: 'rgba(249,115,22,.25)' },
  medium:   { label: 'Médio',   color: C.accent,   bg: C.accentFade,  border: C.accentBdr },
  low:      { label: 'Baixo',   color: C.info,     bg: C.infoFade,    border: 'rgba(96,165,250,.25)' },
};
const STATUS = {
  critical: { label: 'CRÍTICO',    color: C.danger,  bg: C.dangerFade },
  warning:  { label: 'ATENÇÃO',    color: C.warning, bg: 'rgba(249,115,22,.1)' },
  ok:       { label: 'SAUDÁVEL',   color: C.success, bg: C.successFade },
  info:     { label: 'INFORMATIVO',color: C.info,    bg: C.infoFade },
};

const _card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.2)' };


function SeverityBadge({ severity }) {
  const s = SEV[severity] || SEV.medium;
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase',
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 4, padding: '2px 7px',
    }}>{s.label}</span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.info;
  return (
    <span style={{
      fontSize: 11, fontWeight: 800, letterSpacing: '.07em',
      color: s.color, background: s.bg,
      border: `1px solid ${s.color}30`,
      borderRadius: 6, padding: '3px 10px',
    }}>{s.label}</span>
  );
}

function MetricPill({ label, value }) {
  if (!value) return null;
  return (
    <div style={{
      padding: '7px 12px', background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 7, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 70,
    }}>
      <span style={{ fontSize: 16, fontWeight: 800, color: C.accent, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <span style={{ fontSize: 10, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '.07em' }}>{label}</span>
    </div>
  );
}

function ReportHeroDesktop({ d }) {
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const sevLabel = d.severity ? ((SEV[d.severity] || {}).label || d.severity) : null;
  const METRICS  = [
    { icon: '📅', value: d.date     || 'Recente',     label: 'Data'       },
    { icon: '📍', value: d.origin   || 'Interno',     label: 'Origem'     },
    { icon: '⚠️', value: sevLabel   || 'Normal',      label: 'Severidade' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="📊 RELATÓRIO"
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBdr}
        placeholder="📊" placeholderLabel="Relatório"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            {d.status && <StatusBadge status={d.status} />}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: 0, fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

function ReportHeroMobile({ d }) {
  const sevLabel = d.severity ? ((SEV[d.severity] || {}).label || d.severity) : null;
  const METRICS  = [
    { icon: '📅', value: d.date   || 'Recente',  label: 'Data'       },
    { icon: '📍', value: d.origin || 'Interno',  label: 'Origem'     },
    { icon: '⚠️', value: sevLabel || 'Normal',   label: 'Severidade' },
    { icon: '📊', value: 'Relatório',             label: 'Tipo'       },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBdr}`, borderRadius: 5, padding: '2px 8px' }}>📊 RELATÓRIO</span>
            {d.status && <StatusBadge status={d.status} />}
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBdr}
          placeholder="📊"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function ProblemCard({ problem }) {
  const sev = SEV[problem.severity] || SEV.medium;
  return (
    <div style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: problem.description ? 6 : 0 }}>
        <SeverityBadge severity={problem.severity} />
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text, flex: 1 }}>{problem.title}</span>
      </div>
      {problem.description && <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>{problem.description}</p>}
    </div>
  );
}

function ItemList({ items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', gap: 8, fontSize: 14, color: C.text }}>
          <span style={{ color: color || C.textMuted, flexShrink: 0, marginTop: 3 }}>•</span>
          <span style={{ lineHeight: 1.5 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ReportShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <ReportHeroMobile d={d} /> : <ReportHeroDesktop d={d} />}

      {d.summary && (
        <SectionCard title="Resumo Executivo" borderColor={C.info} style={{ marginBottom: 10 }}>
          <ProseText text={d.summary} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      )}
      {!d.summary && d.context && (
        <SectionCard title="Contexto" borderColor={C.accentBdr} style={{ marginBottom: 10 }}>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.context}</p>
        </SectionCard>
      )}

      {d.problems && d.problems.length > 0 && (
        <SectionCard title={`Problemas Encontrados (${d.problems.length})`} borderColor={C.danger} style={{ marginBottom: 10 }}>
          {d.problems.map((p, i) => <ProblemCard key={i} problem={p} />)}
        </SectionCard>
      )}

      {d.metrics && d.metrics.length > 0 && (
        <SectionCard title="Métricas" borderColor={C.accentBdr} style={{ marginBottom: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {d.metrics.map((m, i) => (
              <div key={i} style={{ padding: '10px 12px', background: 'rgba(12,18,32,0.6)', border: `1px solid ${C.border}`, borderRadius: 7 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
                <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>{m.label}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {d.risks && (
        <SectionCard title="Riscos e Impactos" borderColor={C.warning} style={{ marginBottom: 10 }}>
          <ProseText text={d.risks} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      )}

      {d.recommendations && d.recommendations.length > 0 && (
        <SectionCard title="Recomendações" borderColor={C.success} style={{ marginBottom: 10 }}>
          <ItemList items={d.recommendations} color={C.success} />
        </SectionCard>
      )}

      {d.next_steps && d.next_steps.length > 0 && (
        <SectionCard title="Próximos Passos" borderColor={C.accentBdr} style={{ marginBottom: 10 }}>
          <ol style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {d.next_steps.map((s, i) => (
              <li key={i} style={{ fontSize: 14, color: C.text, lineHeight: 1.5 }}>{s}</li>
            ))}
          </ol>
        </SectionCard>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
